import { SHContainer, SHStack } from "@components/design-systems";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { StepName } from "@layouts/stepper/step-name";
import { RootState } from "@redux/store";
import {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useToggle } from "react-use";
import { BusinessMetricSelectionSkeleton } from "../../../components/skeletons/business-metric-selection";
import { AnalysisBusinessMetricCategoriesContainer } from "./components/categories-container";

export const BusinessMetricsSelectionStep: ForwardRefRenderFunction<
  StepCompRef,
  StepCompProps
> = ({ stepNumber }, ref) => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const {
    isDirty,
    isValid,
    platformAnalysis,
    setIsDirty,
    setIsValid,
    loadBusinessMetricSelections,
    updateBusinessMetricSelections,
  } = usePlatformAnalysis();

  const storedSelections = useSelector(
    (state: RootState) => state.platformAnalysis.businessMetricSelections,
  );
  const storedIsLoading = useSelector(
    (state: RootState) => state.platformAnalysis.ui.isLoading,
  );

  // #region States
  const [isLoading, toggleLoading] = useToggle(true);
  // #end region States

  // #region Handlers
  useImperativeHandle(ref, () => ({
    onChangeStep: async (_, isNext) => {
      if (!isNext && !isValid) {
        setIsValid(true);
        return true;
      }
      return await handleOnSubmit();
    },
    onChangeOtherStep: handleOnSubmit,
    onSubmit: handleOnSubmit,
  }));

  const handleOnSubmit = async () => {
    const hasRecalculateData = !!storedSelections?.hasRecalculate;
    if (!isDirty && !hasRecalculateData) return true;
    if (!analysisId || !storedSelections || !isValid) return false;

    const response = await updateBusinessMetricSelections({
      platformAnalysisId: analysisId,
      businessMetricSelections: storedSelections,
    });

    if (response) setIsDirty(false);
    return !!response;
  };
  // #end region Handlers

  // #region Effects
  useEffect(() => {
    if (!analysisId || analysisId === "new") return;
    loadBusinessMetricSelections({
      platformAnalysisId: analysisId,
    }).then((response) => {
      toggleLoading(!response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);
  // #endregion Effects

  return (
    <>
      <SHStack display={isLoading || storedIsLoading ? "block" : "none"}>
        <BusinessMetricSelectionSkeleton />
      </SHStack>

      <SHStack display={!isLoading && !storedIsLoading ? "block" : "none"}>
        <SHContainer
          sx={{
            px: { xs: "16px", lg: 0 },
            py: "25px",
          }}
        >
          <StepName
            name={
              platformAnalysis?.isReview
                ? "What does your client value in a business?"
                : "Business metric selection"
            }
            stepNumber={stepNumber}
          />

          <AnalysisBusinessMetricCategoriesContainer />
        </SHContainer>
      </SHStack>
    </>
  );
};
